/**
 * THE ONE FILE THAT KNOWS WHAT THE API LOOKS LIKE.
 *
 * The backend's notification model is still moving, so the screen never touches a server field directly.
 * Everything it draws comes from NotificationItem, and this adapter is where a server payload becomes one.
 * When the API changes shape, this file changes and nothing else does.
 *
 * The mapping below is a STARTING POINT written against today's `sync_notifications` table plus the fields
 * the three-tab screen needs. Treat the field names as a proposal to the backend, not as a contract that
 * already exists.
 */
import {
  INBOUND_KIND,
  MASTER_KIND,
  MASTER_KIND_LABEL,
  NOTIFICATION_OPERATION,
  NOTIFICATION_OPERATION_LABEL,
  NOTIFICATION_TAB,
  type InboundKind,
  type MasterKind,
  type NotificationItem,
  type NotificationOperation,
  type NotificationTab,
} from '@/types/admin-notification-center';

/** Raw row as the API is expected to return it. Every field optional — a missing one must not crash a list. */
export interface RawNotification {
  /** レセプト同期 — which operation the press was: 'upsert' | 'invalidate' | 'cancel_return'. */
  operation?: string | null;
  /** One entry per rezept, for the two operations whose 内容 names patients. */
  records?: Array<{
    aggregate?: string | null;
    aggregate_id?: string | null;
    outcome?: string | null;
    patient_name?: string | null;
    patient_serial?: string | null;
    submit_yearmonth?: string | null;
  }> | null;
  records_total?: number | null;
  /** マスタ同期 — true when 審査 had never held this record before the send. */
  is_new?: boolean | null;
  /** The 種別 wording the server stored on the row at press time. */
  operation_label?: string | null;
  id: number;
  category?: string | null;
  tab?: string | null;
  /** Today's table has `status` ∈ unread/read/resolved/dismissed and a separate `severity`. */
  status?: string | null;
  severity?: string | null;
  /** If the API ever sends the 種別 outright, it wins over anything derived. */
  level?: string | null;
  /** 対象件数 of the run. */
  total_count?: number | null;
  read_at?: string | null;
  title?: string | null;
  body?: string | null;
  has_drift_csv?: boolean;
  aggregate?: string | null;
  aggregate_id?: string | null;
  attempts?: number | null;
  /** 'A2' | 'R2'. Optional — the adapter falls back to the aggregate name. */
  service_type?: string | null;
  /** Wire event of an inbound message, when the API does not resolve `inbound_kind` itself. */
  event_type?: string | null;
  /** 返戻区分 (RET_KBN). 9 turns a `returned` into 一部不支給. */
  return_kbn?: number | string | null;
  /**
   * レセプト同期 — when the send entered the queue, and when its last job settled.
   *
   * `finished_at === null` WITH a `started_at` is how the API says 「still running」, so the adapter reads
   * that pair instead of needing a separate status field.
   */
  started_at?: string | null;
  finished_at?: string | null;
  /**
   * レセプト同期 — the tally of ONE SEND (one 確定 press: clinic × 提出年月 × service).
   *
   * These two are a REQUEST TO THE BACKEND, not a field that exists today: the screen groups by send, so
   * the API has to aggregate rather than return one row per rezept. Without them the tab would list every
   * rezept of a 500-record press separately.
   */
  success_count?: number | null;
  failure_count?: number | null;
  /** 対象外 — see NotificationSend.outOfScope in the types file for why it is its own bucket. */
  out_of_scope_count?: number | null;
  /**
   * How many times 再送信 has been pressed on this row, and when the last press went out.
   *
   * A re-send does NOT get a row of its own — it folds into the press it repairs, so without these two the
   * screen could not show that anybody had acted. They are deliberately NOT folded into the sync window:
   * a re-send can happen days later, and stretching 同期時間 to cover it renders as 「a sync that took three
   * days」.
   */
  retry_count?: number | null;
  /**
   * One entry per 送信 — the press, then each re-send folded into it, oldest first.
   *
   * Sent as FACTS ONLY (counts + moments). The screen composes every sentence from them; a sentence built
   * server-side would be free to disagree with the numbers printed beside it.
   */
  sends?: {
    correlation_id?: string | null;
    started_at?: string | null;
    finished_at?: string | null;
    total?: number | null;
    success?: number | null;
    failure?: number | null;
    out_of_scope?: number | null;
    running?: number | null;
  }[] | null;
  action_type?: string | null;
  created_at?: string | null;
  clinic_number?: string | null;
  clinic_name?: string | null;
  /**
   * マスタ同期 — the record the press was about.
   *
   * `target_key` is the SENDER'S key: 「980001-3」 for a 治療院, 「4433-1」 for a 保険者. It is a different
   * field from `aggregate_id`, which on a run row is the correlation id, not a record key.
   */
  target_key?: string | null;
  /** マスタ同期 — the name behind target_key (治療院名 or 保険者名). */
  target_name?: string | null;
  patient_name?: string | null;
  /**
   * 受診者番号 — `patients.serial_number`, per clinic.
   *
   * Accepted as a number OR a string because it is an `int` column that the screen treats as an identifier;
   * the adapter settles on one type so no cell has to guess. `serial_number` is honoured as an alias, since
   * that is the name it carries everywhere else in this API.
   */
  patient_serial?: string | number | null;
  serial_number?: string | number | null;
  submit_yearmonth?: string | null;
  inbound_kind?: string | null;
  error?: {
    code?: string | null;
    message?: string | null;
    http_status?: number | null;
    endpoint?: string | null;
    rows?: { field?: string | null; value?: string | null; reason?: string | null }[] | null;
  } | null;
}

/** Which inbox a row belongs to. Falls back to the aggregate name when the API does not say. */
const tabOf = (raw: RawNotification): NotificationTab => {
  const explicit = raw.tab ?? raw.category ?? '';
  if (explicit === NOTIFICATION_TAB.MASTER) return NOTIFICATION_TAB.MASTER;
  if (explicit === NOTIFICATION_TAB.REZEPT) return NOTIFICATION_TAB.REZEPT;
  if (explicit === NOTIFICATION_TAB.INBOUND) return NOTIFICATION_TAB.INBOUND;

  const aggregate = raw.aggregate ?? '';
  if (aggregate.startsWith('rezept-status')) return NOTIFICATION_TAB.INBOUND;
  if (aggregate.startsWith('rezept')) return NOTIFICATION_TAB.REZEPT;
  return NOTIFICATION_TAB.MASTER;
};

/**
 * 結果 — a yes/no the table can colour.
 *
 * Order matters: an explicit `level` wins, then the presence of an error block, then severity. `status` is
 * consulted LAST and only for 'resolved', because on today's table it mostly carries the READ state and a
 * row can be unread and successful at the same time.
 */
const levelOf = (raw: RawNotification): 'info' | 'success' | 'error' => {
  if (raw.level === 'info' || raw.level === 'success' || raw.level === 'error') {
    return raw.level;
  }

  // A send with no finish time is STILL RUNNING. Checked before any failure evidence: a batch that has
  // already logged one bad rezept is not finished, and calling it 失敗 while its other 400 jobs are still in
  // the queue would put a red row on screen that the queue is about to make green.
  if (raw.finished_at === null && raw.started_at != null) return 'info';
  if (raw.error?.code) return 'error';
  if (raw.severity === 'error') return 'error';
  if (raw.status === 'resolved') return 'success';
  return 'success';
};

const INBOUND_KINDS = new Set<string>(Object.values(INBOUND_KIND));

/** RET_KBN 9 — the 返戻 that was partly PAID. Shinsa's AppConstants::CLAIM_IMPORT_RET_KBN_LABELS. */
const RET_KBN_PARTIAL_UNPAID = 9;

/**
 * Which of the FOUR notifiable kinds this row is.
 *
 * 一部不支給 has no wire event: it arrives as `returned` carrying 返戻区分 = 9, so it is resolved here from
 * the pair, exactly as RAH does at the label layer (RezeptStatusHandler.php:2034). An explicit
 * `inbound_kind` from the API still wins, so the backend can settle it server-side later without a change
 * on this side.
 *
 * `reviewed` and `paid` fall through to NULL on purpose. RAH still receives and applies both — the rezept's
 * 審査状況 label moves — they simply do not raise a notification (owner's decision, 2026-08-27). If the
 * backend ever sends one anyway, it lands with no 種別 rather than inventing a fifth tag.
 */
const inboundKindOf = (raw: RawNotification): InboundKind | null => {
  if (raw.inbound_kind && INBOUND_KINDS.has(raw.inbound_kind)) {
    return raw.inbound_kind as InboundKind;
  }

  if (raw.inbound_kind === INBOUND_KIND.RETURNED || raw.event_type === INBOUND_KIND.RETURNED) {
    return Number(raw.return_kbn) === RET_KBN_PARTIAL_UNPAID
      ? INBOUND_KIND.PARTIAL_UNPAID
      : INBOUND_KIND.RETURNED;
  }

  return null;
};

/**
 * aggregate → データ種別. Only the three the pipeline actually pushes; 施術者 and 保険者区分 have constants
 * but no dispatcher, so they are not mapped (see MASTER_KIND in the types file for the evidence).
 *
 * The three address aggregates (prefecture / city / town) are NOT mapped, since 2026-09-07: they travel as
 * a whole-table file whose runner writes no notification at all, so no row ever carries one of these keys.
 * An entry here would only describe rows that cannot exist. Restore all three together, and as ONE kind,
 * if the file push ever gets its own hook — they travel in a single file and a reader wants 「住所」, not
 * which of the three tables it was.
 */
const MASTER_KIND_OF: Record<string, MasterKind> = {
  clinic: MASTER_KIND.CLINIC,
  insurer: MASTER_KIND.INSURER,
  // `town` IS THE WHOLE ADDRESS PUSH, not just the 町名 table. The three address tables go to Shinsa in one
  // file under one version and SyncAddressFileJob keys its state on this one name, so this is the only
  // aggregate the server can send for an address row -- prefecture and city never appear on their own.
  town: MASTER_KIND.ADDRESS,
};

/**
 * The business code out of the aggregate key.
 *
 * Both per-record masters key as 「{code}-{sub_number}」 (ClinicPayloadBuilder / InsurerPayloadBuilder), so
 * the code is everything before the LAST hyphen — not the first: a 公費 保険者番号 like 「0034（障0）」 is
 * bracketed rather than hyphenated today, but splitting on the first hyphen would break the moment one is
 * not.
 *
 * 住所 HAS NO CODE, and returning one would be worse than leaving the cell empty. Its key is the constant
 * 「address-file」, which the hyphen rule above would happily read as the code 「address」 — a value that
 * looks like a 治療院ID, matches nothing, and invites someone to search for it. The row is about the whole
 * master, so コード stays 「-」.
 */
const codeFromKey = (kind: MasterKind | null, key: string): string | null => {
  if (kind === null || kind === MASTER_KIND.ADDRESS) return null;

  const cut = key.lastIndexOf('-');
  return cut > 0 ? key.slice(0, cut) : key || null;
};

/**
 * WHICH OPERATION a レセプト同期 row is about.
 *
 * Reads the server's raw value and validates it against the three we know. An unknown string falls back to
 * `upsert` rather than rendering blank: a row with no 種別 reads as a bug in the screen, while 「レセプト同期」
 * on a row that turns out to be something else is at worst imprecise — and the server is the one that
 * decides, so an unknown value here means the front end is the stale half.
 */
const operationOf = (raw: RawNotification): NotificationOperation => {
  const value = raw.operation ?? '';
  return (Object.values(NOTIFICATION_OPERATION) as string[]).includes(value)
    ? (value as NotificationOperation)
    : NOTIFICATION_OPERATION.UPSERT;
};

/** 受診者番号 out of either field name, as a string. Blank strings count as absent. */
const serialOf = (raw: RawNotification): string | null => {
  const value = raw.patient_serial ?? raw.serial_number;
  if (value == null) return null;

  const text = String(value).trim();
  return text === '' ? null : text;
};

export const toNotificationItem = (raw: RawNotification): NotificationItem => {
  const tab = tabOf(raw);
  const level = levelOf(raw);
  const aggregate = raw.aggregate ?? '';
  const masterKind =
    tab === NOTIFICATION_TAB.MASTER ? (MASTER_KIND_OF[aggregate] ?? null) : null;
  // サービス区分 comes from the AGGREGATE, not from a field: neither rezepts nor r_rezepts has a
  // service_type column, and which of the two tables a rezept lives in IS its service. `service_type` on
  // the payload still wins if the API ever sends one outright.
  const serviceType: 'A2' | 'R2' | null =
    raw.service_type === 'A2' || raw.service_type === 'R2'
      ? raw.service_type
      : aggregate === 'rezept-a2'
        ? 'A2'
        : aggregate === 'rezept-r2'
          ? 'R2'
          : null;

  return {
    id: raw.id,
    tab,
    level,
    // read_at wins when present; otherwise fall back to today's status vocabulary.
    read: raw.read_at != null || (raw.status != null && raw.status !== 'unread'),
    // 日時 — when the NOTIFICATION was raised, which is `created_at`. It used to read `started_at` first,
    // and that was right while the column rendered the run's span; now that the span lives in 内容 the two
    // are different questions and this one must not answer the other.
    occurredAt: raw.created_at ?? raw.started_at ?? new Date(0).toISOString(),
    startedAt: raw.started_at ?? raw.created_at ?? '',
    finishedAt: raw.finished_at ?? null,
    targetLabel:
      tab === NOTIFICATION_TAB.MASTER
        ? (masterKind !== null ? MASTER_KIND_LABEL[masterKind] : (aggregate ?? '-'))
        : tab === NOTIFICATION_TAB.REZEPT
          ? 'レセプト'
          : 'レセプト通知',
    masterKind,
    serviceType,
    // 🔴 target_key, NOT aggregate_id. On a run row aggregate_id is the CORRELATION ID — the grain change
    // that lets one row stand for a whole press — so reading it here chopped a UUID at its last hyphen and
    // printed 「95c294a6-cf2d-4d4c-8d15」 in a column headed コード. The server sends the record's own key
    // (「980001-3」 / 「4433-1」) as target_key since 2026-09-02.
    targetCode: codeFromKey(masterKind, raw.target_key ?? ''),
    // Falls back to clinic_name because the server fills target_name from it for 治療院 runs anyway; the
    // fallback keeps an older API answering this screen.
    targetName: raw.target_name ?? raw.clinic_name ?? null,
    targetKey: raw.target_key ?? '-',
    // 内容 is ONE sentence. `body` first because it is the human one; `title` is the machine-ish fallback.
    summary: raw.body ?? raw.title ?? '',
    // WHAT WAS PRESSED — レセプト同期 only. The label comes from the server (stored on the row at press
    // time); the map here is only a floor for a row written before the field existed, and `null` on the
    // other tabs is meaningful: they have no press.
    operation: tab === NOTIFICATION_TAB.REZEPT ? operationOf(raw) : null,
    operationLabel:
      tab === NOTIFICATION_TAB.REZEPT
        ? (raw.operation_label ?? NOTIFICATION_OPERATION_LABEL[operationOf(raw)])
        : null,
    // An unknown outcome string falls back to 'failure' rather than rendering raw: this cell is read by
    // someone deciding whether a patient's request got through, and a word they cannot classify must err
    // toward 「look at this」, never toward silence.
    records: (raw.records ?? []).map(record => ({
      aggregate: record.aggregate ?? '',
      aggregateId: record.aggregate_id ?? '',
      outcome:
        record.outcome === 'success' || record.outcome === 'running' ? record.outcome : 'failure',
      patientName: record.patient_name ?? null,
      patientSerial: record.patient_serial ?? null,
      submitYearMonth: record.submit_yearmonth ?? null,
    })),
    recordsTotal: raw.records_total ?? 0,
    // 新規 / 更新 on マスタ同期. `undefined` (an older row, or a rezept press) stays null so the sentence
    // can fall back to the neutral wording instead of asserting one of the two.
    isNew: typeof raw.is_new === 'boolean' ? raw.is_new : null,
    clinicNumber: raw.clinic_number ?? null,
    clinicName: raw.clinic_name ?? null,
    patientName: raw.patient_name ?? null,
    // `?? null` and not `|| null`: serial 0 does not exist today (numbering starts at 1), but a falsy check
    // on an identifier is the kind of thing that survives until the one row that has one.
    patientSerial: serialOf(raw),
    submitYearMonth: raw.submit_yearmonth ?? null,
    inboundKind: inboundKindOf(raw),
    // 状態の不一致 rows only. Defaults to false so a row from an older server, or from the mock, simply
    // draws no button rather than one that answers 404.
    hasDriftCsv: raw.has_drift_csv === true,
    // レセプト同期 only — a send's tally. Null elsewhere, where a row is one record.
    totalCount: tab === NOTIFICATION_TAB.INBOUND ? null : (raw.total_count ?? null),
    successCount: tab === NOTIFICATION_TAB.INBOUND ? null : (raw.success_count ?? null),
    failureCount: tab === NOTIFICATION_TAB.INBOUND ? null : (raw.failure_count ?? null),
    outOfScopeCount: tab === NOTIFICATION_TAB.INBOUND ? null : (raw.out_of_scope_count ?? null),
    attempts: raw.attempts ?? 1,
    retryCount: raw.retry_count ?? 0,
    sends: (raw.sends ?? []).map(send => ({
      correlationId: send.correlation_id ?? '',
      startedAt: send.started_at ?? null,
      finishedAt: send.finished_at ?? null,
      total: send.total ?? 0,
      success: send.success ?? 0,
      failure: send.failure ?? 0,
      outOfScope: send.out_of_scope ?? 0,
      running: send.running ?? 0,
    })),
    // Never offered on a success row, whatever the server says: re-pushing something that already landed is
    // the one action on this screen that can overwrite good data.
    retryable: level === 'error' && raw.action_type === 'resync',
    errorDetail: raw.error?.code
      ? {
          code: raw.error.code,
          message: raw.error.message ?? '',
          httpStatus: raw.error.http_status ?? null,
          endpoint: raw.error.endpoint ?? null,
          occurredAt: raw.created_at ?? '',
          rows: (raw.error.rows ?? []).map(r => ({
            field: r.field ?? '',
            value: r.value ?? '',
            reason: r.reason ?? '',
          })),
        }
      : null,
  };
};
